import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import VerifiedBadge from "../components/VerifiedBadge";
import StarRating from "../components/StarRating";
import TrustScoreBadge from "../components/TrustScoreBadge";
import VehicleCard from "../components/VehicleCard";
import {
  User as UserIcon, Loader2, Send, MessageSquare, Star,
  ShieldCheck, AlertCircle, ChevronDown, ChevronUp, Lock, Image as ImageIcon,
} from "lucide-react";

/* ─── Seller profile page ────────────────────────────────────────────── */
export default function SellerProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();
  const [seller, setSeller] = useState(null);
  const [trust, setTrust] = useState(null);
  const [reviews, setReviews] = useState({ items: [], total: 0 });
  const [vehicles, setVehicles] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, t, r, v] = await Promise.allSettled([
        apiClient.get(`/users/${id}`),
        apiClient.get(`/sellers/${id}/trust`),
        apiClient.get(`/sellers/${id}/reviews`, { params: { limit: 50 } }),
        apiClient.get(`/public/seller/${id}`),
      ]);
      if (s.status === "fulfilled") setSeller(s.value.data);
      if (t.status === "fulfilled") setTrust(t.value.data);
      if (r.status === "fulfilled") setReviews(r.value.data);
      if (v.status === "fulfilled") setVehicles(v.value.data.vehicles || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [id]);

  if (busy) return <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!seller) return <div className="max-w-3xl mx-auto p-10 text-[#525252]">{lang === "ar" ? "البائع غير موجود" : "Seller not found"}</div>;

  const isOwner = user?.id === id;
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const kycApproved = seller.verified && seller.kyc_status === "approved";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="seller-profile-page">
      {/* Header */}
      <div className="surface rounded-3xl p-6 sm:p-8">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-20 h-20 rounded-2xl bg-[#0a0a0a] text-[#c9a55c] flex items-center justify-center font-display text-3xl shrink-0">
            {(seller.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] font-medium" data-testid="seller-name">{seller.name}</h1>
              {kycApproved && <VerifiedBadge status="approved" type={seller.kyc_type} lang={lang} size="md" />}
            </div>
            <div className="text-sm text-[#525252] mt-1">
              {seller.role && <span className="capitalize">{seller.role.replace("_", " ")}</span>}
              {seller.city && <> · {seller.city}</>}
              {seller.created_at && <> · {lang === "ar" ? "عضو منذ" : "Member since"} {new Date(seller.created_at).getFullYear()}</>}
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              {trust && <TrustScoreBadge score={trust.trust_score} size="lg" lang={lang} />}
              {trust && <StarRating value={trust.rating_avg} count={trust.rating_count} size="md" lang={lang} />}
            </div>
          </div>
        </div>

        {/* Admin/Owner-only breakdown */}
        {trust?.trust_breakdown_full && (isAdmin || isOwner) && (
          <div className="mt-5 border-t border-[#0a0a0a]/[0.08] pt-4" data-testid="trust-breakdown">
            <button onClick={() => setShowBreakdown((s) => !s)} className="text-xs text-[#8a6f2e] inline-flex items-center gap-1 hover:underline">
              <Lock className="w-3 h-3" />
              {lang === "ar" ? "تفاصيل النقاط (للأدمن وصاحب الحساب فقط)" : "Score breakdown (admin & owner only)"}
              {showBreakdown ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showBreakdown && (
              <dl className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
                {Object.entries(trust.trust_breakdown_full).filter(([k]) => !k.startsWith("_")).map(([k, v]) => (
                  <div key={k} className="flex justify-between bg-[#fafaf7] rounded-lg p-2">
                    <dt className="text-[#525252]">{labelFor(k, lang)}</dt>
                    <dd className={`font-bold ${v > 0 ? "text-[#0a4f3a]" : v < 0 ? "text-red-700" : "text-[#525252]"}`}>{v > 0 ? "+" : ""}{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}
      </div>

      {/* Listings */}
      {vehicles.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-xl text-[#0a0a0a] mb-4">{lang === "ar" ? "إعلانات البائع" : "Active listings"} ({vehicles.length})</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="mt-10" data-testid="reviews-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-display text-xl text-[#0a0a0a]">
            {lang === "ar" ? "المراجعات" : "Reviews"} ({reviews.total})
          </h2>
          {user && !isOwner && (
            <Link to={`/sellers/${id}/review`} className="btn-ghost text-sm" data-testid="write-review-cta">
              <Star className="w-4 h-4" /> {lang === "ar" ? "اكتب مراجعة" : "Write a review"}
            </Link>
          )}
        </div>
        {reviews.items.length === 0 ? (
          <div className="surface rounded-2xl p-10 text-center text-[#6b6b6b]" data-testid="reviews-empty">
            <Star className="w-8 h-8 mx-auto text-[#c9a55c] mb-2" />
            {lang === "ar" ? "لم يكتب أحد مراجعة بعد. كن أول من يقيّم بعد إتمام صفقتك." : "No reviews yet. Be the first to share an experience after a completed deal."}
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.items.map((r) => <ReviewCard key={r.id} r={r} lang={lang} isSeller={isOwner} onReply={load} isAdmin={isAdmin} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function labelFor(k, lang) {
  const M = {
    kyc: ["توثيق الهوية", "Identity verified"],
    profile_complete: ["اكتمال الملف", "Profile complete"],
    sales: ["المبيعات المكتملة", "Completed sales"],
    rating: ["تقييم البائع", "Seller rating"],
    disputes_lost: ["نزاعات خسرها", "Disputes lost"],
    longevity: ["أقدمية العضوية", "Membership age"],
  };
  return (M[k] || [k, k])[lang === "ar" ? 0 : 1];
}

/* ─── Single review card ────────────────────────────────────────────── */
function ReviewCard({ r, lang, isSeller, isAdmin, onReply }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showReplyBox, setShowReplyBox] = useState(false);

  const submit = async () => {
    if (!reply.trim()) return;
    setBusy(true); setErr(null);
    try {
      await apiClient.post(`/reviews/${r.id}/reply`, { body: reply.trim() });
      setShowReplyBox(false); setReply(""); onReply?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const hide = async () => {
    const reason = prompt(lang === "ar" ? "سبب الإخفاء:" : "Reason for hiding:");
    if (!reason) return;
    await apiClient.post(`/admin/reviews/${r.id}/hide`, { reason });
    onReply?.();
  };

  return (
    <div className={`surface rounded-2xl p-5 ${r.hidden ? "opacity-60" : ""}`} data-testid={`review-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StarRating value={r.rating} size="md" showCount={false} lang={lang} />
            {r.verified_purchase && (
              <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 font-semibold"
                style={{ backgroundColor: "rgba(10,79,58,0.10)", color: "#0a4f3a", border: "1px solid rgba(10,79,58,0.30)" }}
                data-testid={`verified-purchase-${r.id}`}>
                <ShieldCheck className="w-3 h-3" />
                {lang === "ar" ? "شراء موثّق" : "Verified purchase"}
              </span>
            )}
            {r.hidden && (
              <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 font-semibold bg-red-50 text-red-700 border border-red-200">
                <Lock className="w-3 h-3" /> {lang === "ar" ? "مخفية بقرار إداري" : "Hidden by admin"}
              </span>
            )}
          </div>
          {r.title && <h3 className="font-display text-lg text-[#0a0a0a] mt-2">{r.title}</h3>}
          <p className="text-sm text-[#3a3a3a] mt-1 leading-relaxed whitespace-pre-wrap">{r.body}</p>
          {r.images?.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {r.images.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="rounded-lg overflow-hidden border border-[#0a0a0a]/[0.08] w-16 h-16">
                  <img src={url} alt={`review-${i}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
          <div className="text-[10px] text-[#8a8a8a] mt-2 inline-flex items-center gap-2">
            <span className="font-semibold text-[#3a3a3a]">{r.buyer_name}</span>
            {r.buyer_verified && <VerifiedBadge status="approved" lang={lang} />}
            <span>·</span>
            <span>{new Date(r.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}</span>
          </div>
        </div>
        {isAdmin && !r.hidden && (
          <button onClick={hide} className="btn-ghost text-[10px] text-red-700 border-red-200" data-testid={`hide-review-${r.id}`}>
            {lang === "ar" ? "إخفاء" : "Hide"}
          </button>
        )}
      </div>

      {r.reply && (
        <div className="mt-4 ms-6 rounded-xl bg-[#fafaf7] border border-[#c9a55c]/30 p-3">
          <div className="text-[10px] font-bold text-[#8a6f2e] uppercase tracking-widest mb-1">{lang === "ar" ? "رد البائع" : "Seller reply"}</div>
          <p className="text-sm text-[#3a3a3a] whitespace-pre-wrap">{r.reply.body}</p>
          <div className="text-[10px] text-[#8a8a8a] mt-1">{new Date(r.reply.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}</div>
        </div>
      )}

      {isSeller && !r.reply && !r.hidden && (
        <div className="mt-3">
          {!showReplyBox ? (
            <button onClick={() => setShowReplyBox(true)} className="btn-ghost text-xs" data-testid={`reply-cta-${r.id}`}>
              <Send className="w-3.5 h-3.5" /> {lang === "ar" ? "رد (مرة واحدة)" : "Reply (one-time)"}
            </button>
          ) : (
            <div>
              <textarea rows={3} className="input-dark" value={reply} onChange={(e) => setReply(e.target.value)} placeholder={lang === "ar" ? "ردّك..." : "Your reply..."} maxLength={1000} data-testid={`reply-input-${r.id}`} />
              {err && <p className="text-xs text-red-700 mt-1">{err}</p>}
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => { setShowReplyBox(false); setReply(""); }} className="btn-ghost text-xs">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
                <button onClick={submit} disabled={busy || !reply.trim()} className="btn-primary text-xs" data-testid={`submit-reply-${r.id}`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {lang === "ar" ? "نشر" : "Post"}
                </button>
              </div>
              <p className="text-[10px] text-[#8a6f2e] mt-1 inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {lang === "ar" ? "الرد لا يمكن تعديله أو حذفه بعد النشر." : "Once posted, this reply can't be edited or deleted."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
